import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, Minus, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useBatmanAuth } from '../context/BatmanAuthContext';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';
import userApi from '../services/userApi';

const Pricing = () => {
  const [tokenAmount, setTokenAmount] = useState(2);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const { isAuthenticated, user, fetchAccount } = useBatmanAuth();
  const navigate = useNavigate();

  const pricePerToken = 10 / 2;
  const minTokens = 2;
  const maxTokens = 50;
  const step = 2;
  const totalPrice = tokenAmount * pricePerToken;

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  const handlePayment = useCallback(async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/pricing' } } });
      return;
    }

    setIsProcessing(true);
    setPaymentStatus(null);
    setStatusMessage('');

    try {
      const amountInPaise = Math.round(totalPrice * 100);
      const orderResponse = await userApi.createPaymentOrder({
        amount: amountInPaise,
        creditType: 'coding',
        creditAmount: tokenAmount,
      });

      const options = {
        key: orderResponse.keyId,
        amount: orderResponse.amount,
        currency: orderResponse.currency || 'INR',
        name: 'Batman App',
        description: `${tokenAmount} Credits`,
        order_id: orderResponse.orderId,
        prefill: {
          name: user?.username || '',
        },
        theme: {
          color: '#ffffff',
        },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            setPaymentStatus('error');
            setStatusMessage('Payment cancelled');
          },
        },
        handler: async function (response) {
          try {
            const verificationResponse = await userApi.verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              creditType: 'coding',
              creditAmount: tokenAmount,
            });

            if (verificationResponse.success) {
              setPaymentStatus('success');
              setStatusMessage(`Payment successful! ${verificationResponse.creditsAdded} credits added.`);
              await fetchAccount();
            } else {
              setPaymentStatus('error');
              setStatusMessage(verificationResponse.message || 'Payment verification failed');
            }
          } catch (verifyError) {
            setPaymentStatus('error');
            setStatusMessage('Payment verification failed. Please contact support.');
          } finally {
            setIsProcessing(false);
          }
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', function (response) {
        setIsProcessing(false);
        setPaymentStatus('error');
        setStatusMessage(response.error?.description || 'Payment failed. Please try again.');
      });
      razorpay.open();
    } catch (error) {
      setIsProcessing(false);
      setPaymentStatus('error');
      setStatusMessage(error.message || 'Failed to initiate payment. Please try again.');
    }
  }, [isAuthenticated, navigate, totalPrice, tokenAmount, user, fetchAccount]);

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-5xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">CREDIT PACKS</span>
          <h1 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-tight">Simple, transparent pricing</h1>
          <p className="text-white/50 text-lg">Purchase credits instantly and top up anytime.</p>
        </div>
      </section>

      <section className="pb-20 px-6 md:px-12 lg:px-20">
        <div className="max-w-5xl mx-auto border border-white/10 p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-light text-white">CREDITS</h2>
            <CreditCard className="h-5 w-5 text-white/50" />
          </div>

          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setTokenAmount((prev) => Math.max(minTokens, prev - step))}
              className="p-2 border border-white/20 text-white/80 hover:text-white"
            >
              <Minus className="h-4 w-4" />
            </button>
            <input
              type="number"
              value={tokenAmount}
              min={minTokens}
              max={maxTokens}
              step={step}
              onChange={(e) => setTokenAmount(Math.max(minTokens, Math.min(maxTokens, Number(e.target.value || minTokens))))}
              className="w-32 px-4 py-2 bg-white/[0.02] border border-white/10 text-white"
            />
            <button
              onClick={() => setTokenAmount((prev) => Math.min(maxTokens, prev + step))}
              className="p-2 border border-white/20 text-white/80 hover:text-white"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-8 text-white/70">
            <p>Price per credit: ₹{pricePerToken.toFixed(2)}</p>
            <p className="text-3xl text-white mt-2">Total: ₹{totalPrice.toFixed(2)}</p>
          </div>

          <button
            onClick={handlePayment}
            disabled={isProcessing}
            className="px-6 py-3 bg-white text-black font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Pay Now'}
          </button>

          {paymentStatus && (
            <div className={`mt-6 p-4 border ${paymentStatus === 'success' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-red-500/30 bg-red-500/10 text-red-300'}`}>
              {statusMessage}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Pricing;
